import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Archive, Truck, Package2, BookOpen, ArrowRight } from 'lucide-react';

const REPORT_CARDS = [
  {
    id: 'inventory',
    title: 'Inventory Reports',
    description: 'Status board, expiry/calibration calendar, and utilization stats for all inventory items. Drill down into any item\'s full checkout history.',
    icon: Archive,
    path: '/reports/inventory',
    color: 'bg-blue-50',
    iconColor: 'text-blue-600',
    highlights: ['Status Board', 'Expiry Calendar', 'Utilization Stats', 'Item History'],
  },
  {
    id: 'fleet',
    title: 'Fleet Reports',
    description: 'Trip-level P&L summaries, driver performance comparisons, and vehicle utilization analytics across any date range.',
    icon: Truck,
    path: '/reports/fleet',
    color: 'bg-purple-50',
    iconColor: 'text-purple-600',
    highlights: ['Trip Summary & P&L', 'Driver Performance', 'Vehicle Performance'],
  },
  {
    id: 'dockets',
    title: 'Docket Reports',
    description: 'Full docket listing with filters for shipment type, status, and date range, plus analytics breakdown by mode and pipeline stage.',
    icon: Package2,
    path: '/reports/dockets',
    color: 'bg-amber-50',
    iconColor: 'text-amber-600',
    highlights: ['Docket Summary', 'By Shipment Type', 'By Status'],
  },
  {
    id: 'bookings',
    title: 'Booking Reports',
    description: 'Booking request summary with status tracking (Pending / Confirmed / Rejected) and analytics by load type, trip category and top customers.',
    icon: BookOpen,
    path: '/reports/bookings',
    color: 'bg-green-50',
    iconColor: 'text-green-600',
    highlights: ['Booking Summary', 'Analytics', 'Top Customers'],
  },
];

export default function ReportsLanding() {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-dark font-manrope">Reports</h1>
        <p className="text-sm text-text-secondary mt-1">Select a report category to get started</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {REPORT_CARDS.map(card => {
          const Icon = card.icon;
          return (
            <Card
              key={card.id}
              data-testid={`report-card-${card.id}`}
              onClick={() => navigate(card.path)}
              className="p-6 cursor-pointer hover:shadow-lg transition-all border-2 hover:border-accent-blue group"
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${card.color}`}>
                  <Icon className={`w-7 h-7 ${card.iconColor}`} />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-text-dark font-manrope group-hover:text-accent-blue transition-colors">
                    {card.title}
                  </h2>
                  <p className="text-sm text-text-secondary mt-1 leading-relaxed">{card.description}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {card.highlights.map(h => (
                      <span key={h} className="text-xs bg-gray-100 text-text-secondary px-2.5 py-1 rounded-full font-ibm">
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <span className="flex items-center gap-1 text-sm text-accent-blue font-medium font-ibm group-hover:gap-2 transition-all">
                  View Reports <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
